import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import './UAEFDLDataManagement.scss';
import { Table, Modal, Button, Form } from 'react-bootstrap';
import GHGEmissionService from '../../../../core/service/GHGEmissionService';
import SubStdService from '../../../../core/service/SubStdService';
import StandardService from '../../../../core/service/StandardService';
import { LoaderContext, RegulationContext } from '../../../../App';
import { LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY } from '../../../../core/constants/common';
import Utils from '../../../../core/utilities/Utils';
import { toast } from 'react-toastify';
import { BsFileEarmarkFill, BsDownload, BsX, BsPaperclip } from 'react-icons/bs';
import { FcFinePrint } from 'react-icons/fc';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';

const UAEFDLDataManagementTable = ({ t, filters, isEditMode, onDataChange, searchTerm, currentView, onTableDataUpdate, clearEditableData, refreshTimestamp }) => {
  const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsDat.profile.company.id;
  const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
  const { glbRegulation } = useContext(RegulationContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const { setLoaderDisplay } = useContext(LoaderContext);
  const [tableRows, setTableRows] = useState([]);
  const [branchNames, setBranchNames] = useState([]);
  const [editableData, setEditableData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [uomList, setUOMList] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const prevDataRef = useRef({ tableRows: [], branchNames: [], editableData: {} });
  const requestInFlightRef = useRef(false);
  const fetchIdRef = useRef(0);
  const lastRequestKeyRef = useRef('');
  const fetchTimeoutRef = useRef(null);

  // Stable signature for filter params to avoid effect runs from object reference changes
  const filterSignature = useMemo(() => {
    if (!filters) return '';
    return JSON.stringify({
      glbRegulationId,
      periodFrom: filters.periodFrom,
      periodTo: filters.periodTo,
      scope: filters.scope,
      category: filters.category,
      businessUnitIds: Array.isArray(filters.businessUnitIds) ? filters.businessUnitIds : (filters.businessUnitIds || '')
    });
  }, [glbRegulationId, filters?.periodFrom, filters?.periodTo, filters?.scope, filters?.category, filters?.businessUnitIds]);

  // Attachment modal state variables
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [tempAttachments, setTempAttachments] = useState([]);
  const [selectedFile, setSelectedFile] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [messageConfirmation, setMessageConfirmation] = useState('');
  const fileInputRef = useRef();

  // Comment editing state variables
  const [isCommentEdit, setIsCommentEdit] = useState(false);
  const [dpComments, setDPComments] = useState('');
  const [tempComments, setTempComments] = useState('');

  useEffect(() => {
    getUOMs();
  }, []);

  useEffect(() => {
    if (!glbRegulationId || !filters || !filterSignature) return;

    // Combined view: only fetch when scope and businessUnitIds are set (avoids API call with empty payload).
    // Entity/workbench views: allow fetch so user can select entity or see data after selection.
    const hasScope = Array.isArray(filters.scope)
      ? filters.scope.length > 0
      : Boolean(filters.scope && String(filters.scope).trim() !== '');
    const hasBusinessUnits = Array.isArray(filters.businessUnitIds)
      ? filters.businessUnitIds.length > 0
      : Boolean(filters.businessUnitIds);
    const filtersReady = currentView === 'combined'
      ? (hasScope && hasBusinessUnits)
      : true;

    if (!filtersReady) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      return;
    }

    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    const DEBOUNCE_MS = 150;
    fetchTimeoutRef.current = setTimeout(() => {
      fetchTimeoutRef.current = null;
      lastRequestKeyRef.current = `${filterSignature}|${refreshTimestamp}`;
      getTableData();
    }, DEBOUNCE_MS);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTableData uses glbRegulationId, filters from closure; filterSignature encodes them
  }, [filterSignature, refreshTimestamp]);

  // Clear table data when view changes
  useEffect(() => {
    if (currentView) {
      setTableRows([]);
      setBranchNames([]);
      // Don't clear editableData here to preserve user edits
    }
  }, [currentView]);

  // Clear editable data when clearEditableData prop changes
  useEffect(() => {
    if (clearEditableData) {
      setEditableData({});
      setValidationErrors({});
      // Notify parent that editable data is cleared
      if (onDataChange) {
        onDataChange({});
      }
    }
  }, [clearEditableData, onDataChange]);

  // Notify parent component when table data changes
  useEffect(() => {
    const currentData = { tableRows, branchNames, editableData, validationErrors };
    const prevData = prevDataRef.current;

    // Only update if data has actually changed
    const hasChanged =
      JSON.stringify(currentData.tableRows) !== JSON.stringify(prevData.tableRows) ||
      JSON.stringify(currentData.branchNames) !== JSON.stringify(prevData.branchNames) ||
      JSON.stringify(currentData.editableData) !== JSON.stringify(prevData.editableData) ||
      JSON.stringify(currentData.validationErrors) !== JSON.stringify(prevData.validationErrors);

    if (onTableDataUpdate && hasChanged) {
      prevDataRef.current = currentData;
      onTableDataUpdate(currentData);
    }
  }, [tableRows, branchNames, editableData, validationErrors]);

  const getUOMs = () => {
    StandardService.getUnitsOfMeasure()
      .then(res => {
        if (res.status !== 200) return;
        setUOMList(res.data.data);
      })
      .catch((error) => { });
  };

  const isEntityColumn = (columnAccessor) => {
    // System columns that should NOT have UOM dropdowns
    const systemColumns = [
      'scope',
      'category',
      'metricName',
      'emissionInTCO2e',
      'unit',
      'attachments',
      'comments',
      'emissionFactors'
    ];

    // Check if it's a system column - if so, it's NOT an entity column
    if (systemColumns.includes(columnAccessor)) {
      return false;
    }

    // Check if it's a branch column (from API data)
    if (branchNames.includes(columnAccessor)) {
      return true;
    }

    // Hardcode the specific column names we see in the image
    const knownEntityColumns = [
      'Test',
      'Bluesky builders 1',
      'Test A',
      'Bluesky builders',
      'Edge Solutions Sharjah',
      'Edge Solutions Dubai',
      'Edge Solutions Abudabi',
      'Edge Solutions Chennai',
      'Edge Solutions Mumbai'
    ];

    if (knownEntityColumns.includes(columnAccessor)) {
      return true;
    }

    // Check if it's an entity column based on common patterns
    const entityColumnPatterns = [
      'test',
      'bluesky',
      'edge solutions',
      'entity',
      'branch',
      'location',
      'site',
      'facility'
    ];

    const accessorLower = columnAccessor.toLowerCase();
    return entityColumnPatterns.some(pattern => accessorLower.includes(pattern));
  };

  const checkValidation = (rowKey, columnKey, value, uomValue) => {
    const newValidationErrors = { ...validationErrors };
    const errorKey = `${rowKey}_${columnKey}`;

    // Check if there's a value > 0 but no UOM selected
    const hasValue = value && value.toString().trim() !== '' && parseFloat(value) > 0;
    const hasUOM = uomValue && uomValue.toString().trim() !== '';

    if (hasValue && !hasUOM) {
      newValidationErrors[errorKey] = true;
    } else {
      delete newValidationErrors[errorKey];
    }

    setValidationErrors(newValidationErrors);
  };

  const hasValidationErrors = () => {
    return Object.keys(validationErrors).length > 0;
  };

  const getTableData = () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    const currentFetchId = fetchIdRef.current + 1;
    fetchIdRef.current = currentFetchId;

    setIsLoading(true);
    setLoaderDisplay(true);

    GHGEmissionService.getDVData(glbRegulationId, filters)
      .then((response) => {
        if (fetchIdRef.current !== currentFetchId) return;
        const apiData = response.data.data;

        // Handle null or empty data
        if (!apiData || apiData === null) {
          setBranchNames([]);
          setTableRows([]);
          setIsLoading(false);
          setLoaderDisplay(false);
          requestInFlightRef.current = false;
          return;
        }

        const branches = getAllBranches(apiData);
        const rows = getTableRows(apiData, branches);

        setBranchNames(branches);
        setTableRows(rows);

        // Clear editable data when refreshing from API
        if (clearEditableData) {
          setEditableData({});
          setValidationErrors({});
          // Notify parent that editable data is cleared
          if (onDataChange) {
            onDataChange({});
          }
        }

        setIsLoading(false);
        setLoaderDisplay(false);
        requestInFlightRef.current = false;
      })
      .catch((error) => {
        if (fetchIdRef.current !== currentFetchId) return;
        console.error('Error fetching GHG emission data:', error);
        // Clear data on error as well
        setBranchNames([]);
        setTableRows([]);
        setEditableData({});
        setValidationErrors({});
        setIsLoading(false);
        setLoaderDisplay(false);
        requestInFlightRef.current = false;
      });
  };

  // Attachment modal functions
  const showAttachmentDetails = (row) => {
    setSelectedMetric(row);

    // Collect all attachments and aggregate comments (metric, branch.comment/remark, attachment.comment)
    let allAttachments = [];
    const commentsParts = [];
    const getComment = (obj) => {
      if (!obj || typeof obj !== 'object') return '';
      const t = obj.comment ?? obj.remark ?? obj.comments ?? obj.remarks ?? '';
      return typeof t === 'string' ? t.trim() : '';
    };

    if (row.originalMetric) {
      const mc = getComment(row.originalMetric);
      if (mc) commentsParts.push(mc);
      if (row.originalMetric.branches && Array.isArray(row.originalMetric.branches)) {
        row.originalMetric.branches.forEach(branch => {
          const bc = getComment(branch);
          if (bc) commentsParts.push(bc);
          if (branch.attachments && Array.isArray(branch.attachments)) {
            branch.attachments.forEach(attachment => {
              allAttachments.push({
                ...attachment,
                branchName: branch.name
              });
              const ac = getComment(attachment);
              if (ac) commentsParts.push(ac);
            });
          }
        });
      }
    }
    const comments = commentsParts.filter(Boolean).join('\n');

    setAttachedFiles(allAttachments);
    setTempAttachments([...allAttachments]);
    setDPComments(comments);
    setTempComments(comments);
    setIsCommentEdit(false); // Start in view mode
    setShowAttachmentModal(true);
  };

  const handleAttachmentModalClose = () => {
    setShowAttachmentModal(false);
    setSelectedMetric(null);
    setAttachedFiles([]);
    setTempAttachments([]);
    setSelectedFile({});
    setMessageConfirmation('');
    setDPComments('');
    setTempComments('');
    setIsCommentEdit(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const uploadAttachments = (fileUploaded) => {
    if (fileUploaded) {
      const formData = new FormData();
      formData.append("document", fileUploaded);
      setLoaderDisplay(true);
      SubStdService.uploadAttachments(formData).then(res => {
        setLoaderDisplay(false);
        if (res.status === 200 || res.status === 201) {
          let files = [...tempAttachments];
          files.push(res.data);
          setTempAttachments(files);
          fileInputRef.current.value = null;

          // Only update table data immediately if not in edit mode
          if (!isCommentEdit) {
            updateTableDataWithNewAttachment(res.data);
          }
        }
      }).catch((error) => {
        setLoaderDisplay(false);
        toast.error('Failed to upload file', {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
    } else {
      toast.warning('Please select a file');
    }
  };

  const handleFileSelect = (event) => {
    if (event.target.files[0].size <= 1024 * 1024 * 2) {
      const fileUploaded = event.target.files[0];
      uploadAttachments(fileUploaded);
    } else {
      fileInputRef.current.value = null;
      toast.warning('Upload files less than 2MB', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000
      });
    }
  };

  const updateTableDataWithNewAttachment = (newAttachment) => {
    // Update the table rows to reflect the new attachment count
    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.dataPointId === selectedMetric.dataPointId) {
          return {
            ...row,
            attachments: row.attachments + 1,
            originalMetric: {
              ...row.originalMetric,
              branches: row.originalMetric.branches.map(branch => ({
                ...branch,
                attachments: branch.attachments ? [...branch.attachments, newAttachment] : [newAttachment]
              }))
            }
          };
        }
        return row;
      });
    });
  };

  const showConfirm = (data) => {
    setMessageConfirmation('Are you sure you want to delete this file?');
    setSelectedFile(data);
    setShowConfirmation(true);
  };

  const confirmDelete = (val) => {
    setShowConfirmation(false);
    if (val) {
      deleteFile(selectedFile.id);
    }
  };

  const deleteFile = (id) => {
    const index = tempAttachments.findIndex(x => x.id === id);
    if (index >= 0) {
      const updatedAttachments = [...tempAttachments];
      updatedAttachments.splice(index, 1);
      setTempAttachments(updatedAttachments);
    }
  };

  const downloadAttachment = (id) => {
    setLoaderDisplay(true);
    SubStdService.getDocumentsAttached(id)
      .then((response) => {
        setLoaderDisplay(false);
        let fetchDataModified = `data:${response.data.mimeType};base64,${response.data.file}`;
        let link = document.createElement("a");
        link.href = fetchDataModified;
        link.download = response.data.name;
        link.click();
      })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const noAttachmentLabel = () => {
    return (
      <div>
        <FcFinePrint className="me-2" /> No attachments available!
      </div>
    );
  };

  const setupEdit = () => {
    setIsCommentEdit(true);
    setTempComments(dpComments);
  };

  const cancelEdit = () => {
    setIsCommentEdit(false);
    setTempComments(dpComments);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const saveAttachments = () => {
    if (!selectedMetric) {
      toast.error('No metric selected', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!glbRegulationId) {
      toast.error('No regulation selected', {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Get the rowKey for this metric to check editableData
    const rowKey = `${selectedMetric.scope}-${selectedMetric.category}-${selectedMetric.metricName}`;

    // Find the branch name and data that matches the selected metric
    // We need to find which branch we're saving attachments for
    let branchName = null;
    let branchId = null;
    let branchDataId = null;
    let branchValue = null;
    let branchUom = null;

    // First, try to find the branch from the original metric branches
    if (selectedMetric.originalMetric && selectedMetric.originalMetric.branches) {
      // If there's only one branch, use it
      if (selectedMetric.originalMetric.branches.length === 1) {
        const branch = selectedMetric.originalMetric.branches[0];
        branchName = branch.name;
        branchId = branch.branchId || branch.id;
        branchDataId = branch.id;
        branchValue = branch.value;
        branchUom = branch.uom;
      } else {
        // If multiple branches, try to find the one that matches branchNames
        // or use the first one with attachments/comments
        for (const branch of selectedMetric.originalMetric.branches) {
          if (branch.name && branchNames.includes(branch.name)) {
            branchName = branch.name;
            branchId = branch.branchId || branch.id;
            branchDataId = branch.id;
            branchValue = branch.value;
            branchUom = branch.uom;
            break;
          }
        }
        // If still not found, use the first branch
        if (!branchName && selectedMetric.originalMetric.branches.length > 0) {
          const branch = selectedMetric.originalMetric.branches[0];
          branchName = branch.name;
          branchId = branch.branchId || branch.id;
          branchDataId = branch.id;
          branchValue = branch.value;
          branchUom = branch.uom;
        }
      }
    }

    // Check editableData for any unsaved changes that should override original values
    if (branchName && editableData[rowKey]) {
      const editableRowData = editableData[rowKey];
      
      // Check if there's an edited value for this branch
      if (editableRowData[branchName] !== undefined && editableRowData[branchName] !== null && editableRowData[branchName] !== '') {
        branchValue = editableRowData[branchName];
      }
      
      // Check if there's an edited UOM for this branch
      if (editableRowData[`${branchName}_uom`] !== undefined && editableRowData[`${branchName}_uom`] !== null && editableRowData[`${branchName}_uom`] !== '') {
        branchUom = editableRowData[`${branchName}_uom`];
      }
    }

    // If we still don't have branch data, try to get it from the row structure
    if (!branchName && branchNames.length > 0) {
      // Use the first branch name as fallback
      branchName = branchNames[0];
      const branchData = selectedMetric[branchName];
      if (branchData) {
        branchId = branchData.id;
        branchDataId = branchData.id;
        branchValue = branchData.value;
        // Try to get uom from originalMetric branches
        if (selectedMetric.originalMetric && selectedMetric.originalMetric.branches) {
          const branch = selectedMetric.originalMetric.branches.find(b => b.name === branchName);
          if (branch) {
            branchUom = branch.uom;
          }
        }
      }
    }

    // Parse the value using Utils.parseLocaleNumber if it exists, otherwise use 0
    // Ensure we always send a number (0) instead of empty string or NaN
    let parsedValue = 0;
    
    // Check if branchValue is a valid non-empty value
    const hasValidValue = branchValue !== null && 
                         branchValue !== undefined && 
                         branchValue !== '' && 
                         String(branchValue).trim() !== '';
    
    if (hasValidValue) {
      const parsed = Utils.parseLocaleNumber(String(branchValue));
      // Check if parseLocaleNumber returned a valid number (not NaN)
      parsedValue = isNaN(parsed) ? 0 : parsed;
    }
    
    // Ensure parsedValue is always a number, never a string
    parsedValue = typeof parsedValue === 'number' && !isNaN(parsedValue) ? parsedValue : 0;

    const reportingPeriod = filters?.period || filters?.periodFrom || new Date().toISOString().split('T')[0];

    setLoaderDisplay(true);

    // Prepare branch data to save with actual value and uom
    // If no UOM value exists, send empty string
    const parsedUom = branchUom !== null && branchUom !== undefined && branchUom !== '' 
      ? branchUom 
      : '';

    const branchDataToSave = [{
      dataPointId: selectedMetric.dataPointId,
      branchId: branchId || branchName,
      id: branchDataId || branchId || branchName,
      value: parsedValue,
      uom: parsedUom,
      remark: tempComments,
      attachments: tempAttachments
    }];

    SubStdService.saveBranchDataPoint(
      glbRegulationId,
      reportingPeriod,
      { dataPoints: branchDataToSave }
    ).then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200) return;

      toast.success("Comments and attachments saved successfully", {
        position: toast.POSITION.TOP_RIGHT,
      });

      // Update the comments state
      setDPComments(tempComments);
      setIsCommentEdit(false);

      // Optimistically update table rows so attachment count, Comments column, and tooltip reflect saved comment/attachments
      if (selectedMetric && branchName != null && selectedMetric.dataPointId != null) {
        const savedAttachments = Array.isArray(tempAttachments) && tempAttachments.length > 0 ? [...tempAttachments] : [];
        setTableRows(prevRows =>
          prevRows.map(row => {
            if (row.dataPointId !== selectedMetric.dataPointId) return row;
            if (!row.originalMetric || !Array.isArray(row.originalMetric.branches)) return row;
            const updatedBranches = row.originalMetric.branches.map(b => {
              if (b.name !== branchName) return b;
              return {
                ...b,
                attachments: savedAttachments,
                remark: tempComments != null ? tempComments : b.remark,
                comment: tempComments != null ? tempComments : b.comment
              };
            });
            const updatedMetric = { ...row.originalMetric, branches: updatedBranches };
            const newTotalAttachments = updatedBranches.reduce(
              (sum, b) => sum + (Array.isArray(b.attachments) ? b.attachments.length : 0),
              0
            );
            const newComments = getCommentsFromMetric(updatedMetric);
            return {
              ...row,
              attachments: newTotalAttachments,
              comments: newComments,
              originalMetric: updatedMetric
            };
          })
        );
      }

      // Close the modal (UI already updated via optimistic update; no need to wait for getTableData)
      handleAttachmentModalClose();
    }).catch((error) => {
      setLoaderDisplay(false);
      toast.error(`Failed to save comments and attachments: ${error.response?.data?.message || error.message}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
    });
  };

  // Extract all unique branch names from the API data
  const getAllBranches = (data) => {
    const branchesSet = new Set();

    // Handle null or empty data
    if (!data || data === null) {
      return [];
    }

    ['Scope 1', 'Scope 2', 'Scope 3'].forEach(scopeKey => {
      if (data[scopeKey]) {
        Object.values(data[scopeKey]).forEach(metricsArr => {
          if (Array.isArray(metricsArr)) {
            metricsArr.forEach(metric => {
              if (metric.branches && Array.isArray(metric.branches)) {
                metric.branches.forEach(branch => {
                  if (branch && branch.name) {
                    branchesSet.add(branch.name);
                  }
                });
              }
            });
          }
        });
      }
    });
    return Array.from(branchesSet);
  };

  // Get comment text from branch or attachment (API may use comment, remark, comments, etc.)
  const getCommentText = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    const text = obj.comment ?? obj.remark ?? obj.comments ?? obj.remarks ?? '';
    return typeof text === 'string' ? text.trim() : '';
  };

  // Aggregate comments from metric (metric + branches + attachments) for display/tooltip. Safe for null/undefined.
  const getCommentsFromMetric = (metric) => {
    try {
      if (!metric || typeof metric !== 'object') return '';
      const parts = [];
      const mc = getCommentText(metric);
      if (mc) parts.push(mc);
      if (metric.branches && Array.isArray(metric.branches)) {
        metric.branches.forEach(branch => {
          const bc = getCommentText(branch);
          if (bc) parts.push(bc);
          if (branch && branch.attachments && Array.isArray(branch.attachments)) {
            branch.attachments.forEach(att => {
              const ac = getCommentText(att);
              if (ac) parts.push(ac);
            });
          }
        });
      }
      return parts.filter(Boolean).join('\n') || '';
    } catch {
      return '';
    }
  };

  // Transform API data into table rows with branch columns
  const getTableRows = (data, branchNames) => {
    const rows = [];

    // Handle null or empty data
    if (!data || data === null) {
      return [];
    }

    ['Scope 1', 'Scope 2', 'Scope 3'].forEach(scopeKey => {
      if (data[scopeKey]) {
        Object.entries(data[scopeKey]).forEach(([category, metricsArr]) => {
          if (Array.isArray(metricsArr)) {
            metricsArr.forEach((metric, metricIndex) => {
              // Calculate total attachments and aggregate comments from branches/attachments
              let totalAttachments = 0;
              const commentsParts = [];
              const metricComment = getCommentText(metric);
              if (metricComment) commentsParts.push(metricComment);
              if (metric.branches && Array.isArray(metric.branches)) {
                metric.branches.forEach(branch => {
                  const branchComment = getCommentText(branch);
                  if (branchComment) commentsParts.push(branchComment);
                  if (branch.attachments && Array.isArray(branch.attachments)) {
                    totalAttachments += branch.attachments.length;
                    branch.attachments.forEach(att => {
                      const attComment = getCommentText(att);
                      if (attComment) commentsParts.push(attComment);
                    });
                  }
                });
              }
              const commentsText = commentsParts.filter(Boolean).join('\n') || '';

              const row = {
                scope: scopeKey,
                category,
                metricName: metric.name,
                emissionInTCO2e: metric.emissionCombinedInTCO2e,
                unit: metric.unit,
                // Use calculated total attachments from branches
                attachments: totalAttachments,
                comments: commentsText,
                emissionFactors: metric.emissionFactor !== null && metric.emissionFactor !== undefined ? metric.emissionFactor : 0,
                // Add dataPointId for save functionality
                dataPointId: metric.id,
                // Store original metric data for reference
                originalMetric: metric
              };

              // Add branch data
              branchNames.forEach(branchName => {
                const branch = metric.branches.find(b => b.name === branchName);
                row[branchName] = branch ? {
                  id: branch.id,
                  value: branch.value,
                  name: branch.name
                } : null;
              });

              rows.push(row);
            });
          }
        });
      }
    });

    return rows;
  };

  // Group data by scope and category
  const groupRows = (rows) => {
    const grouped = {};
    rows.forEach(row => {
      if (!grouped[row.scope]) grouped[row.scope] = {};
      if (!grouped[row.scope][row.category]) grouped[row.scope][row.category] = [];
      grouped[row.scope][row.category].push(row);
    });
    return grouped;
  };

  // Filter rows based on search term, scope, and category filters
  const filteredRows = useMemo(() => {
    let filtered = tableRows;

    // Filter by search term
    if (searchTerm && searchTerm.trim() !== '') {
      filtered = filtered.filter(row =>
        row.metricName &&
        row.metricName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by scope (if filters.scope is an array and not empty)
    if (filters && filters.scope && Array.isArray(filters.scope) && filters.scope.length > 0) {
      filtered = filtered.filter(row => filters.scope.includes(row.scope));
    }

    // Filter by category (if filters.category is an array and not empty)
    if (filters && filters.category && Array.isArray(filters.category) && filters.category.length > 0) {
      filtered = filtered.filter(row => filters.category.includes(row.category));
    }

    return filtered;
  }, [tableRows, searchTerm, filters?.scope, filters?.category]);

  const grouped = groupRows(filteredRows);

  // Build columns dynamically
  const columns = useMemo(() => {
    const baseColumns = [
      { Header: 'Scope', accessor: 'scope', isSticky: true },
      { Header: 'Category', accessor: 'category', isSticky: true },
      { Header: 'Emission Sources', accessor: 'metricName', isSticky: true },
      { Header: 'Emission in tCO2e', accessor: 'emissionInTCO2e' },
      { Header: 'Unit', accessor: 'unit' },
    ];

    // Add branch columns first (before entity-specific columns)
    const branchColumns = branchNames.map(branch => ({
      Header: branch,
      accessor: branch,
      isSticky: false,
    }));

    // Add entity view specific columns after branch columns
    if (currentView === 'entity' && filters.businessUnitIds && filters.businessUnitIds.length > 0) {
      const entityColumns = [
        { Header: 'Emission factors', accessor: 'emissionFactors', isSticky: false },
        { Header: 'Attachments', accessor: 'attachments', isSticky: false },
        { Header: 'Comments', accessor: 'comments', isSticky: false }
      ];
      return [...baseColumns, ...branchColumns, ...entityColumns];
    }

    return [...baseColumns, ...branchColumns];
  }, [branchNames, currentView, filters.businessUnitIds]);



  const getStickyLeft = (columnIndex) => {
    let left = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i] && columns[i].isSticky) {
        // Use fixed widths for sticky columns
        if (columns[i].accessor === 'scope') {
          left += 100;
        } else if (columns[i].accessor === 'category') {
          left += 200;
        } else {
          left += 200; // default width
        }
      }
    }
    return left;
  };

  const handleCellChange = (rowKey, columnKey, value) => {
    const newEditableData = {
      ...editableData,
      [rowKey]: {
        ...editableData[rowKey],
        [columnKey]: value
      }
    };

    setEditableData(newEditableData);

    // Check validation for entity columns (any non-system column)
    const systemColumns = ['emissionInTCO2e', 'unit', 'attachments', 'comments', 'emissionFactors'];
    if (!systemColumns.includes(columnKey)) {
      const uomValue = newEditableData[rowKey]?.[`${columnKey}_uom`] || '';
      checkValidation(rowKey, columnKey, value, uomValue);
    }

    // Notify parent component of data changes
    if (onDataChange) {
      onDataChange(newEditableData);
    }
  };

  const handleUOMChange = (rowKey, columnKey, value) => {
    const newEditableData = {
      ...editableData,
      [rowKey]: {
        ...editableData[rowKey],
        [`${columnKey}_uom`]: value
      }
    };

    setEditableData(newEditableData);

    // Check validation for entity columns (any non-system column)
    const systemColumns = ['emissionInTCO2e', 'unit', 'attachments', 'comments', 'emissionFactors'];
    const cellValue = newEditableData[rowKey]?.[columnKey] || '';
    if (!systemColumns.includes(columnKey)) {
      // Force validation check when UOM changes
      checkValidation(rowKey, columnKey, cellValue, value);
    }

    // Notify parent component of data changes
    if (onDataChange) {
      onDataChange(newEditableData);
    }
  };

  const getCellValue = (row, columnKey) => {
    const rowKey = `${row.scope}-${row.category}-${row.metricName}`;

    if (editableData[rowKey] && editableData[rowKey][columnKey] !== undefined) {
      return editableData[rowKey][columnKey];
    }

    // Handle branch columns with new structure
    if (branchNames.includes(columnKey)) {
      return row[columnKey] ? row[columnKey].value : '';
    }

    return row[columnKey] !== undefined && row[columnKey] !== null ? row[columnKey] : '';
  };

  const getUOMValue = (row, columnKey) => {
    const rowKey = `${row.scope}-${row.category}-${row.metricName}`;

    if (editableData[rowKey] && editableData[rowKey][`${columnKey}_uom`] !== undefined) {
      return editableData[rowKey][`${columnKey}_uom`];
    }

    // Handle branch columns with new structure
    if (branchNames.includes(columnKey)) {
      return row[columnKey] ? row[columnKey].uom : '';
    }

    return '';
  };

  // Render attachment cell as circle with count
  const renderAttachmentCell = (value, row) => {
    // Get attachment details and comment from the original metric data
    let attachmentDetails = [];
    if (row.originalMetric && row.originalMetric.branches) {
      row.originalMetric.branches.forEach(branch => {
        if (branch.attachments && Array.isArray(branch.attachments)) {
          branch.attachments.forEach(attachment => {
            attachmentDetails.push({
              name: attachment.name || 'Unknown file',
              mimeType: attachment.mimeType || 'application/octet-stream',
              branchName: branch.name
            });
          });
        }
      });
    }

    const commentsText = getCommentsFromMetric(row.originalMetric) || (row.comments && String(row.comments).trim()) || '';
    let tooltipText = attachmentDetails.length > 0
      ? attachmentDetails.map(att => `${att.name} (${att.branchName})`).join('\n')
      : `${value} attachment${value > 1 ? 's' : ''}`;
    if (commentsText) {
      tooltipText += (tooltipText ? '\n\n' : '') + 'Comment: ' + commentsText;
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          padding: '4px 8px',
          borderRadius: '16px',
          minWidth: 'fit-content'
        }}
        title={tooltipText}
        onClick={() => showAttachmentDetails(row)}
      >
        <BsPaperclip style={{ fontSize: '14px' }} />
        <span 
        style={{ 
          fontSize: '11px', 
          fontWeight: 'bold',
          background: '#19a81f',
          padding: '0px 6px',
          borderRadius: '5px',
          color: '#fff',
          position: 'relative',
          top: '-8px'
        }}
        
        >
          {value}
        </span>
      </div>
    );
  };

  const renderCell = (row, column) => {
    const value = getCellValue(row, column.accessor);



    // Render attachment cell
    if (column.accessor === 'attachments') {
      return renderAttachmentCell(value, row);
    }

    // Render emission source cell with hover expansion
    if (column.accessor === 'metricName') {
      const displayText = value && value.length > 40 ? value.substring(0, 40) + '...' : value;
      const fullText = value;
      return (
        <div
          style={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            if (value && value.length > 40) {
              e.target.style.whiteSpace = 'normal';
              e.target.style.wordWrap = 'break-word';
              e.target.style.overflow = 'visible';
              e.target.textContent = fullText;
            }
          }}
          onMouseLeave={(e) => {
            if (value && value.length > 40) {
              e.target.style.whiteSpace = 'nowrap';
              e.target.style.wordWrap = 'normal';
              e.target.style.overflow = 'hidden';
              e.target.textContent = displayText;
            }
          }}
        >
          {displayText}
        </div>
      );
    }

    // Render comments cell
    if (column.accessor === 'comments') {
      return (
        <div
          style={{
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={value}
        >
          {value}
        </div>
      );
    }

    // Render center-aligned cells for specific columns
    if (column.accessor === 'emissionInTCO2e' || column.accessor === 'unit' || column.accessor === 'emissionFactors') {
      const displayValue = column.accessor === 'emissionInTCO2e' && (value !== '' && value != null)
        ? Utils.roundToDecimalPlacesIfNumber(value, 4)
        : value;
      return (
        <div
          style={{
            textAlign: 'center',
          }}
        >
          {displayValue}
        </div>
      );
    }

    // Handle entity columns in edit mode (show both input and dropdown)
    // Any column that is not sticky and not a system column should have UOM dropdown
    if (isEditMode && !column.isSticky && column.accessor !== 'emissionInTCO2e' && column.accessor !== 'unit' && column.accessor !== 'attachments' && column.accessor !== 'comments' && column.accessor !== 'emissionFactors') {
      const rowKey = `${row.scope}-${row.category}-${row.metricName}`;
      const uomValue = getUOMValue(row, column.accessor);
      const hasValidationError = validationErrors[`${rowKey}_${column.accessor}`];

      return (
        <div style={{ display: 'flex', gap: '2px', position: 'relative', zIndex: 1, overflow: 'hidden', minHeight: '32px', width: '100%' }}>
          {/* <input
            className='form-control'
            type="text"
            value={value}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Only allow numbers, decimal points, and negative signs
              if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
                handleCellChange(rowKey, column.accessor, inputValue);
              }
            }}
            style={{
              border: hasValidationError ? '2px solid #dc3545' : '',
            }}
            onFocus={(e) => e.target.select()}
          /> */}
          <input
            className="form-control"
            type="text"
            value={value}
            onChange={(e) => {
              const inputValue = e.target.value;
              // Allow numbers, one decimal point, optional minus, max 4 digits after decimal
              const decimalPattern = /^-?\d*\.?\d{0,4}$/;
              if (inputValue === '' || decimalPattern.test(inputValue)) {
                handleCellChange(rowKey, column.accessor, inputValue);
              }
            }}
            onBlur={(e) => {
              const inputValue = e.target.value.trim();
              if (inputValue === '') return;
              const formatted = Utils.roundToDecimalPlacesIfNumber(inputValue, 4);
              // Only update if we got a valid number string and it differs (avoids overwriting with invalid or no-op)
              if (formatted !== '' && formatted !== inputValue && !isNaN(parseFloat(formatted))) {
                handleCellChange(rowKey, column.accessor, formatted);
              }
            }}
            style={{
              border: hasValidationError ? '2px solid #dc3545' : '',
            }}
            onFocus={(e) => e.target.select()}
          />

          <select
            className='form-select'
            value={uomValue}
            onChange={(e) => handleUOMChange(rowKey, column.accessor, e.target.value)}
            style={{
              border: hasValidationError ? '2px solid #dc3545' : '',
              boxShadow: hasValidationError ? '0 1px 3px rgba(220, 53, 69, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <option value="">-</option>
            {(() => {
              // Check if relevantUoms exist for this metric
              const relevantUoms = row.originalMetric?.relevantUoms;

              if (relevantUoms && relevantUoms.length > 0) {
                // Show only relevant UOMs
                return relevantUoms
                  .sort((a, b) => a.localeCompare(b))
                  .map(uom => {
                    const uomData = uomList.find(l => l.inputUnit === uom);
                    return (
                      <option key={uom} value={uom}>
                        {uomData?.inputUnitName || uom}
                      </option>
                    );
                  });
              } else {
                // Show all UOMs
                return uomList.map(uom => (
                  <option key={uom.id} value={uom.inputUnit}>
                    {uom.inputUnitName || uom.inputUnit}
                  </option>
                ));
              }
            })()}
          </select>
          {hasValidationError && (
            <div
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                width: '16px',
                height: '16px',
                backgroundColor: '#dc3545',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '10px',
                fontWeight: 'bold',
                zIndex: 1001
              }}
              title="UOM is required when value is provided"
            >
              !
            </div>
          )}
        </div>
      );
    }



    // Handle entity columns in non-edit mode (show only value, no UOM)
    if (!column.isSticky && column.accessor !== 'emissionInTCO2e' && column.accessor !== 'unit' && column.accessor !== 'attachments' && column.accessor !== 'comments' && column.accessor !== 'emissionFactors') {
      return (
        <div>
          {value}
        </div>
      );
    }

    // Default cell rendering with 14px font size
    return (
      <div >
        {value}
      </div>
    );
  };

  // Render no data found message
  const renderNoDataMessage = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={columns.length} className="text-center" style={{ padding: '40px 20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <span style={{ color: '#6c757d', }}>Loading data...</span>
            </div>
          </td>
        </tr>
      );
    }

    if (Object.keys(grouped).length === 0) {
      return (
        <tr>
          <td colSpan={columns.length} className="text-center" style={{ padding: '40px 20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #dee2e6'
              }}>
                <span style={{ fontSize: '24px', color: '#6c757d' }}>📊</span>
              </div>
              <div style={{ color: '#6c757d', fontSize: '16px', fontWeight: '500' }}>
                {searchTerm ? 'No data found for your search' : 'No data available'}
              </div>
              <div style={{ color: '#adb5bd', }}>
                {searchTerm ? 'Try adjusting your search criteria' : 'Please check your filters or try a different period'}
              </div>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <div className="ghg-emission-table">
      <div style={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
        <Table responsive bordered hover>
          <thead>
            <tr>
              {columns.map((column, idx) => (
                  //   <th
                  //   key={idx}
                  //   style={{
                  //     width: column.accessor === 'scope' ? 100 : column.accessor === 'category' ? 200 : column.accessor === 'metricName' ? 300 : column.accessor === 'attachments' ? 120 : column.accessor === 'comments' ? 200 : column.accessor === 'emissionFactors' ? 150 : (!column.isSticky && column.accessor !== 'emissionInTCO2e' && column.accessor !== 'unit' && column.accessor !== 'attachments' && column.accessor !== 'comments' && column.accessor !== 'emissionFactors' ? 150 : 150),
                  //     minWidth: column.accessor === 'scope' ? 100 : column.accessor === 'category' ? 200 : column.accessor === 'metricName' ? 300 : column.accessor === 'attachments' ? 120 : column.accessor === 'comments' ? 200 : column.accessor === 'emissionFactors' ? 150 : (!column.isSticky && column.accessor !== 'emissionInTCO2e' && column.accessor !== 'unit' && column.accessor !== 'attachments' && column.accessor !== 'comments' && column.accessor !== 'emissionFactors' ? 150 : 150),
                  //     maxWidth: column.isSticky ? (column.accessor === 'scope' ? 100 : column.accessor === 'category' ? 200 : 200) : 'none',
                  //     position: column.isSticky ? 'sticky' : 'relative',
                  //     left: column.isSticky ? getStickyLeft(idx) : 'auto',
                  //     zIndex: column.isSticky ? 10 : 1,
                  //     // borderRight: column.isSticky ? '1px solid #dee2e6' : '1px solid #dee2e6',
                  //     boxSizing: 'border-box'
                  //   }}
                  // >
                <th
                  key={idx}
                  style={{
                    width: column.accessor === 'scope' ? 100 : column.accessor === 'category' ? 200 : column.accessor === 'metricName' ? 300 : column.accessor === 'attachments' ? 120 : column.accessor === 'comments' ? 200 : 150,
                    minWidth: column.accessor === 'scope' ? 100 : column.accessor === 'category' ? 200 : column.accessor === 'metricName' ? 300 : column.accessor === 'attachments' ? 120 : column.accessor === 'comments' ? 200 : 150,
                    maxWidth: column.isSticky ? (column.accessor === 'scope' ? 100 : 200) : 'none',
                    position: column.isSticky ? 'sticky' : 'relative',
                    left: column.isSticky ? getStickyLeft(idx) : 'auto',
                    zIndex: column.isSticky ? 10 : 1,
                    // borderRight: column.isSticky ? '1px solid #dee2e6' : '1px solid #dee2e6',
                    boxSizing: 'border-box',
                    backgroundColor:'#fff'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{
                      flex: 1,
                      textAlign: column.accessor === 'emissionInTCO2e' || column.accessor === 'unit' || column.accessor === 'emissionFactors' ? 'center' : 'left'
                    }}>
                      {column.Header}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderNoDataMessage() || Object.entries(grouped).map(([scope, categories]) => {
              let scopeRowCount = Object.values(categories).reduce((sum, rows) => sum + rows.length, 0);
              let scopeRendered = false;
              return Object.entries(categories).map(([category, rows], catIdx) => {
                let categoryRendered = false;
                return rows.map((row, rowIdx) => (
                  <tr key={`${row.scope}-${row.category}-${row.metricName}`}>
                    {!scopeRendered && (
                      <td
                        rowSpan={scopeRowCount}
                        style={{
                          width: 100,
                          minWidth: 100,
                          maxWidth: 100,
                          position: 'sticky',
                          left: getStickyLeft(0),
                          backgroundColor: 'rgb(248 251 251)',
                          zIndex: 100,
                          // borderRight: '1px solid #dee2e6',
                          boxSizing: 'border-box'
                        }}
                      >
                        {scope}
                      </td>
                    )}
                    {!categoryRendered && (
                      <td
                        rowSpan={rows.length}
                        style={{
                          width: 200,
                          minWidth: 200,
                          maxWidth: 200,
                          position: 'sticky',
                          left: getStickyLeft(1),
                          backgroundColor: 'rgb(248 251 251)',
                          zIndex: 90,
                          // borderRight: '2px solid #dee2e6',
                          boxSizing: 'border-box'
                        }}
                      >
                        {category}
                      </td>
                    )}
                    {columns.slice(2).map((col, cidx) => (
                      // <td
                      //   key={cidx}
                      //   className={!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 'entity-column' : ''}
                      //   style={{
                      //     width: col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : col.accessor === 'metricName' ? 300 : col.accessor === 'attachments' ? 120 : col.accessor === 'comments' ? 200 : col.accessor === 'emissionFactors' ? 150 : (!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 150 : 150),
                      //     minWidth: col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : col.accessor === 'metricName' ? 300 : col.accessor === 'attachments' ? 120 : col.accessor === 'comments' ? 200 : col.accessor === 'emissionFactors' ? 150 : (!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 150 : 150),
                      //     // maxWidth: col.isSticky ? (col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : 200) : 'none',
                      //     maxWidth: col.isSticky ? (col.accessor === 'scope' ? 100 : 200) : 'none',
                      //     position: col.isSticky ? 'sticky' : 'relative',
                      //     left: col.isSticky ? getStickyLeft(cidx + 2) : 'auto',
                      //     backgroundColor: col.isSticky ? 'rgb(248 249 250 / 75%)' : 'inherit',
                      //     zIndex: col.isSticky ? 80 : (!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 70 : 1),
                      //     // borderRight: col.isSticky ? '2px solid #dee2e6' : '1px solid #dee2e6',
                      //     boxSizing: 'border-box',
                      //     overflow: 'visible',
                      //     verticalAlign: 'middle'
                      //   }}
                      // >
                      <td
                        key={cidx}
                        className={!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 'entity-column' : ''}
                        style={{
                          width: col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : col.accessor === 'metricName' ? 300 : col.accessor === 'attachments' ? 120 : col.accessor === 'comments' ? 200 : 150,
                          minWidth: col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : col.accessor === 'metricName' ? 300 : col.accessor === 'attachments' ? 120 : col.accessor === 'comments' ? 200 : 150,
                          // maxWidth: col.isSticky ? (col.accessor === 'scope' ? 100 : col.accessor === 'category' ? 200 : 200) : 'none',
                          maxWidth: col.isSticky ? (col.accessor === 'scope' ? 100 : 200) : 'none',
                          position: col.isSticky ? 'sticky' : 'relative',
                          left: col.isSticky ? getStickyLeft(cidx + 2) : 'auto',
                          backgroundColor: col.isSticky ? 'rgb(248 251 251)' : 'inherit',
                          zIndex: col.isSticky ? 80 : (!col.isSticky && col.accessor !== 'emissionInTCO2e' && col.accessor !== 'unit' && col.accessor !== 'attachments' && col.accessor !== 'comments' && col.accessor !== 'emissionFactors' ? 70 : 1),
                          // borderRight: col.isSticky ? '2px solid #dee2e6' : '1px solid #dee2e6',
                          boxSizing: 'border-box',
                          overflow: 'visible',
                          verticalAlign: 'middle'
                        }}
                      >
                        {renderCell(row, col)}
                      </td>
                    ))}
                    {categoryRendered = true}
                    {scopeRendered = true}
                  </tr>
                ));
              });
            })}
          </tbody>
        </Table>
      </div>

      {/* Attachment Details Modal */}
      <Modal
        show={showAttachmentModal}
        onHide={handleAttachmentModalClose}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            Attachment Details - {selectedMetric?.metricName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isCommentEdit ? (
            // Edit mode
            <div>
              <div className="p-1">
                <label className='mb-1'><b>Comments</b></label>
                <textarea
                  className='form-control'
                  rows="5"
                  value={tempComments}
                  placeholder="Provide your comments here"
                  onChange={(e) => { setTempComments(e.target.value) }}
                />
              </div>

              <div className="p-1">
                <label className='mb-1'><b>Upload New Document</b></label>
                <div className='d-flex'>
                  <input
                    className='form-control'
                    type="file"
                    id="imageUpload"
                    accept=".pdf, .docx, .png, .jpg, .xls, .xlsx"
                    onChange={(e) => handleFileSelect(e)}
                    onClick={(e) => {
                      e.target.value = null;
                    }}
                    ref={fileInputRef}
                  />
                </div>
              </div>

              <hr />

              <div className="p-1">
                <label className='mb-1'><b>Attached Documents</b></label>
                <div className='attachedFiles_boxx p-1'>
                  {tempAttachments?.length === 0 && noAttachmentLabel()}
                  {
                    tempAttachments.map((files, index) => {
                      return (
                        <div key={index} className="attachedFiles_boxIn">
                          <span className="attach_icon"><BsFileEarmarkFill /></span>
                          <span className="file-name">{files.name}</span>
                          <span className="attach_Closeicon" onClick={() => downloadAttachment(files.externalId)}>
                            <BsDownload />
                          </span>
                          <span className="attach_Closeicon" onClick={() => showConfirm(files)}>
                            <BsX />
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          ) : (
            // View mode
            <div>
              <div className="p-1">
                <label className='mb-1'><b>Comments</b></label>
                {dpComments ? (
                  <p>{dpComments}</p>
                ) : (
                  <p><FcFinePrint className="me-2" /> No comments added</p>
                )}
              </div>

              <hr />

              <div className="p-1">
                <label className='mb-1'><b>Attached Documents</b></label>
                <div className='attachedFiles_boxx p-1'>
                  {tempAttachments?.length === 0 && noAttachmentLabel()}
                  {
                    tempAttachments.map((files, index) => {
                      return (
                        <div key={index} className="attachedFiles_boxIn">
                          <span className="attach_icon"><BsFileEarmarkFill /></span>
                          <span className="file-name">{files.name}</span>
                          <span className="attach_Closeicon" onClick={() => downloadAttachment(files.externalId)}>
                            <BsDownload />
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          {isCommentEdit ? (
            <>
              <Button variant="secondary" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveAttachments} className="sbmBtn">
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handleAttachmentModalClose}>
                Close
              </Button>
              <Button variant="primary" onClick={setupEdit} className="sbmBtn">
                Edit
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal for File Delete */}
      {showConfirmation && (
        <ConfirmationModal
          modalShow={showConfirmation}
          messageText={messageConfirmation}
          callbackModal={confirmDelete}
          title="Confirm Delete"
          confirmBtn="Delete"
          CancelBtn="Cancel"
        />
      )}
    </div>
  );
};

export default withTranslation()(UAEFDLDataManagementTable);